import { useEffect, useState } from 'react';

import { Dialog } from '@/components/common/Dialog';
import { AddressMismatchPayload } from '@/models/DocumentProgressMessage';
import { generateDocumentRecordUrl } from '@/services/api/document-records/api';

const font = "'Manrope', sans-serif";
const C = { text: '#f3f3f3', muted: '#bbb7af', subtle: '#737373', border: 'rgba(255,255,255,0.08)', accent: '#c0ff02' };

export interface AddressMismatchDialogProps {
  open: boolean;
  payload?: AddressMismatchPayload;
  isLoading?: boolean;
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Dismisses without action — pending upload stays for another reviewer.
   */
  onDismiss?: () => void;
}

export const AddressMismatchDialog = ({
  open,
  payload,
  isLoading,
  error,
  onConfirm,
  onCancel,
  onDismiss,
}: AddressMismatchDialogProps) => {
  const [overrideChecked, setOverrideChecked] = useState(false);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setOverrideChecked(false);
      setDownloadError(undefined);
    }
  }, [open, payload?.documentId]);

  const extractedDisplay =
    payload?.extractedAddress && payload.extractedAddress.trim().length > 0
      ? payload.extractedAddress
      : 'No address detected in document.';

  const handleViewDocument = async () => {
    if (!payload?.documentRecordId) return;
    setIsFetchingUrl(true);
    setDownloadError(undefined);
    try {
      const url = await generateDocumentRecordUrl({ id: payload.documentRecordId });
      if (typeof url === 'string' && url.length > 0) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setDownloadError("Couldn't generate a link.");
      }
    } catch {
      setDownloadError("Couldn't open the document. Try again.");
    } finally {
      setIsFetchingUrl(false);
    }
  };

  const canConfirm = overrideChecked && !isLoading && !!payload?.documentId;

  return (
    <Dialog
      open={open}
      width={520}
      onClose={isLoading ? undefined : onDismiss ?? onCancel}
      disableClose={isLoading}
      disableBackdropClick
      containerProps={{ gap: 0, p: 0 }}>
      <div style={{ padding: 25 }}>
        {/* Header */}
        <div style={{ fontSize: 17, fontWeight: 600, color: C.text, fontFamily: font, marginBottom: 6 }}>
          Address mismatch
        </div>
        <div style={{ fontSize: 13, color: C.subtle, fontFamily: font, lineHeight: 1.6, marginBottom: 24 }}>
          The address in this document doesn't seem to match the property. Review and confirm it belongs here to continue.
        </div>

        {/* Address comparison */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AddressBlock label="Listed property" value={payload?.propertyAddress || '—'} />
          <AddressBlock label="Found in document" value={extractedDisplay} />

          {/* Document link */}
          {payload?.documentRecordId && (
            <div>
              <div style={labelStyle}>Document</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <button
                  onClick={handleViewDocument}
                  disabled={isFetchingUrl}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: isFetchingUrl ? 'default' : 'pointer',
                    fontFamily: font,
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: isFetchingUrl ? 0.5 : 1,
                  }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                  </svg>
                  {payload.documentName ?? 'View document'}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </button>
                {isFetchingUrl && <span style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>Loading…</span>}
              </div>
              {downloadError && <div style={{ fontSize: 12, color: '#ff6b6b', fontFamily: font, marginTop: 4 }}>{downloadError}</div>}
            </div>
          )}

          {/* Confidence */}
          {payload && (
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>
              {(payload.addressConfidence * 100).toFixed(0)}% address match confidence
            </div>
          )}

          {/* Confirm checkbox */}
          <div
            onClick={() => !isLoading && setOverrideChecked((v) => !v)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, cursor: isLoading ? 'default' : 'pointer',
              padding: '12px 14px', borderRadius: 10, transition: 'border-color 0.15s',
              background: overrideChecked ? 'rgba(192,255,2,0.04)' : 'var(--tx-fill)',
              border: 0,
            }}>
            <div style={{
              width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
              border: `1.5px solid ${overrideChecked ? C.accent : '#555'}`,
              background: overrideChecked ? C.accent : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
            }}>
              {overrideChecked && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span style={{ fontSize: 14, color: C.text, fontFamily: font, lineHeight: 1.5 }}>
              This document belongs to the selected property
            </span>
          </div>

          {/* Error */}
          {error && <div style={{ fontSize: 12, color: '#ff6b6b', fontFamily: font }}>{error}</div>}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={onCancel} disabled={isLoading} style={{ ...btnGhost, opacity: isLoading ? 0.4 : 1 }}>
              Cancel upload
            </button>
            <button
              onClick={onConfirm}
              disabled={!canConfirm}
              style={{
                ...btnPrimary,
                background: canConfirm ? C.accent : 'var(--tx-fill)',
                color: canConfirm ? 'var(--tx-text)' : C.subtle,
                cursor: canConfirm ? 'pointer' : 'not-allowed',
              }}>
              {isLoading ? 'Processing…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: '#737373', fontFamily: font,
letterSpacing: '0.06em',
};

const AddressBlock = ({ label, value }: { label: string; value?: string | null }) => (
  <div>
    <div style={labelStyle}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: font, marginTop: 4 }}>
      {value ?? '—'}
    </div>
  </div>
);

const btnBase: React.CSSProperties = {
  height: 40, padding: '0 24px', borderRadius: 10, fontSize: 14,
  fontWeight: 600, fontFamily: font, letterSpacing: '-0.02em',
  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
};
const btnPrimary: React.CSSProperties = { ...btnBase };
const btnGhost: React.CSSProperties = { ...btnBase, background: 'rgba(255,255,255,0.06)', color: C.muted, border: 0 };
